import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { EnvService } from './env.service';
import { map, catchError } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { TokenService } from './token.service';
import { NotificationService } from './notification.service';
import { Router } from '@angular/router';
import { FormGroup } from '@angular/forms';
import { FormBase } from '../models/forms';

interface Request {
  uri: string;
  options: {
    responseType: 'json';
    body: any;
    headers: HttpHeaders;
  };
}

interface ExtraArgs {
  server?: string;
  extraUri?: string;
  notify?: boolean;
  dynamicForm?: IDynamicForm;
  file?: boolean;
}
interface IDynamicForm {
  builder: FormBase<unknown>[];
  form: FormGroup;
}

@Injectable()

export class HttpService {

  defaultErrorMessage = 'Parece que algo ha ido mal, intente nuevamente.';

  defaultExtraArgs: ExtraArgs = {
    server: 'back',
    extraUri: '',
    notify: false,
    file: false,
  };

  constructor(
    private router: Router,
    private http: HttpClient,
    private env: EnvService,
    private token: TokenService,
    private notification: NotificationService,
  ) { }

  request(
    method: string,
    api: string,
    params?: any,
    extraArgs: ExtraArgs = this.defaultExtraArgs,
    contentType?: string
  ): Observable<any> {
    const args = this.prepare(api, params, extraArgs);
    return this.http.request(method, args.uri, args.options)
      .pipe(map((response) => {
        if (extraArgs.file) return response;
        if (response['status'] === 200) {
          if (response['message'] && extraArgs.notify) {
            this.notification.show(response['message'], 'success');
          }
        } else {
          this.handlerError(response);
        }
        return response['content'];
      }),
        catchError(({ error }) =>
          of(this.handleCatchError(error, extraArgs?.dynamicForm))
        ));
  }

  prepare(
    api: string,
    params: any = null,
    extraArgs: ExtraArgs = this.defaultExtraArgs,
  ): Request {
    const server = (extraArgs.server) ? extraArgs.server : this.defaultExtraArgs.server;
    const extraUri = (extraArgs.extraUri) ? extraArgs.extraUri : this.defaultExtraArgs.extraUri;
    const response: any = {};
    response['uri'] = this.env.api(server, api) + extraUri;
    response['options'] = {};
    response['options']['responseType'] = extraArgs.file ? 'blob' : 'json';

    if (this.token.isValid()) {
      response['options']['headers'] = new HttpHeaders({ 'Authorization': `Bearer ${this.token.get('access_token')}` });
    }

    if (params !== null) {
      response['options']['body'] = params;
    }

    return response;
  }

  private enrichFileExtensionError(message: string): string {
    const lower = message?.toLowerCase() ?? '';
    if (lower.includes('extensi') && (lower.includes('válid') || lower.includes('valid'))) {
      return `${message} El archivo puede haber sido renombrado; vuelva a exportarlo en el formato correcto.`;
    }
    return message;
  }

  handlerError(error) {
    let message = error['message'];
    if (!error['message']) {
      message = this.defaultErrorMessage;
    }
    if (error['status'] === 401) {
      this.router.navigate(['login']);
    }
    this.notification.show(message, 'danger', undefined, undefined, 'error_outline');
    throw error;
  }

  handleCatchError(error, dynamicForm: IDynamicForm) {
    if (error?.status === 422) {
      if (dynamicForm !== undefined) {
        Object.keys(error.error).forEach((key: string) => {
          dynamicForm.form.controls[key].setErrors({ invalidForm: true });
          dynamicForm.form.controls[key].markAsTouched();
          dynamicForm.builder.find(res => res.key === key).errors = error.error[key];
        });

        return;
      }

      const payload = error?.error;
      if (typeof payload === 'string') {
        this.notification.show(this.enrichFileExtensionError(payload), 'danger', undefined, undefined, 'error_outline');
        return;
      }
      if (payload && typeof payload === 'object') {
        Object.values(payload).forEach((value: any) => {
          (Array.isArray(value) ? value : [value]).forEach((msg: any) => {
            if (typeof msg === 'string') {
              this.notification.show(this.enrichFileExtensionError(msg), 'danger', undefined, undefined, 'error_outline');
            }
          });
        });
        return;
      }

      return;
    }

    if (error?.error) {
      this.notification.show(error.error, 'danger', undefined, undefined, 'error_outline');

      return false;
    }
  }
}
